import React, { PropsWithChildren, useContext } from 'react'
import { useForm } from 'react-hook-form'
import { Button, ButtonModel } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'

// hooks
import useMessages from '../../../hooks/useMessages'

// styles
import * as SC from './Step1ServiceParameterSelectionStyles'
import { RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT } from '../../../styles/constants'

// components
import ReservationBookingModalLayout from '../ReservationBookingModalLayout/ReservationBookingModalLayout'
import RenderPrice from '../../RenderPrice/RenderPrice'
import RadioButtonsFormField from '../../../formFields/RadioButtonsFormField/RadioButtonsFormField'
import HookFormField from '../../../formFields/HookFormField'

// types
import {
	CmsParameterSelectionBannerData,
	ReservationBookingSelectedFormValues,
	ReservationBookingServiceParameterSelectionForm,
	SalonServiceResponse,
	ServiceCategoryParameterData
} from '../../../types/types'
import { RadioButtonOption } from '../../../formFields/RadioButtonsFormField/types'

// utils
import { FORM, RESERVATION_BOOKING_MODAL_STEP } from '../../../utils/enums'
import { getModalSubtypeSelectedEvent } from '../../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../../utils/dataLayer'
import HeaderContent from '../HeaderContent/HeaderContent'
import { getLinkAttributes } from '../../../utils/helper'
import { AppContext } from '../../../utils/appProvider'

type Props = PropsWithChildren<{
	selectedFormValues: ReservationBookingSelectedFormValues
	headerImage: string | undefined
	selectedIndustryID: string | undefined
	selectedServiceData: SalonServiceResponse['service']
	serviceCategoryParameterData: NonNullable<ServiceCategoryParameterData>
	onClose: () => void
	onBackButtonClick?: () => void
	handleSubmitParameterSelectionFrom: (values: ReservationBookingServiceParameterSelectionForm) => void
	cmsData: CmsParameterSelectionBannerData
}>

const Step1ServiceParameterSelection = (props: Props) => {
	const {
		onClose,
		onBackButtonClick,
		selectedFormValues,
		selectedIndustryID,
		serviceCategoryParameterData,
		headerImage,
		selectedServiceData,
		handleSubmitParameterSelectionFrom,
		cmsData
	} = props
	const { image: promoBanner } = cmsData
	const { messages } = useMessages()
	const { locale } = useIntl()
	const { productionEnvironment } = useContext(AppContext)

	const { control, handleSubmit, watch } = useForm<ReservationBookingServiceParameterSelectionForm>({
		defaultValues: {
			serviceCategoryParameterValueID: selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION].serviceCategoryParameterValueID
		},
		values: {
			serviceCategoryParameterValueID: selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION].serviceCategoryParameterValueID
		}
	})

	const serviceCategoryParameterValueIDValue = watch('serviceCategoryParameterValueID')
	const selectedServiceCategoryParameterValue = serviceCategoryParameterData.values.find((value) => value.id === serviceCategoryParameterValueIDValue)

	// TODO: skontrolovat dlhsi text, ci nerozbije layout
	const serviceCategoryParameterOptions: RadioButtonOption[] =
		serviceCategoryParameterData.values.map((value) => ({
			id: value.id,
			label: value.value || value.id,
			layout: 'vertical',
			description: (
				<RenderPrice
					from={value.priceAndDurationData.priceFrom}
					to={value.priceAndDurationData.priceTo}
					fromSecondary={value.priceAndDurationData.priceFromSecondary}
					toSecondary={value.priceAndDurationData.priceToSecondary}
				/>
			)
		})) || []

	// NOTE: Button from Notino style-guide library doesn't recognize "form" as a prop, however, when passed to the component, it works fine
	const unknownButtonProps = {
		form: FORM.RESERVATION_BOOKING_MODAL_SERVICE_PARAMETER_SELECTION
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} as any

	const picture = (
		<SC.Picture>
			{/* WebP variants */}
			{promoBanner.desktopWebP && (
				<source srcSet={promoBanner.desktopWebP} type='image/webp' media={`(min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT})`} />
			)}
			{promoBanner.mobileWebP && <source srcSet={promoBanner.mobileWebP} type='image/webp' />}

			{/* JPEG fallback */}
			<source srcSet={promoBanner.desktop} media={`(min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT})`} />
			<source srcSet={promoBanner.mobile} />

			{/* Fallback <img> tag for maximum compatibility */}
			<img src={promoBanner.desktop} alt={promoBanner.alt} />
		</SC.Picture>
	)

	return (
		<ReservationBookingModalLayout
			title={serviceCategoryParameterData.name ?? messages['Service parameter']}
			headerImage={headerImage}
			onBackButtonClick={onBackButtonClick}
			onClose={onClose}
			headerContent={<HeaderContent title={selectedServiceData.category.child.child?.name ?? ''} />}
			footerContent={
				<Button
					{...unknownButtonProps}
					type={'submit'}
					buttonStyle={ButtonModel.Styles.primary}
					fullWidth
					disabled={!serviceCategoryParameterValueIDValue}
					onClick={() => {
						if (serviceCategoryParameterValueIDValue) {
							const event = getModalSubtypeSelectedEvent({
								category: selectedIndustryID,
								subcategory: selectedServiceData.category.child?.id,
								type: selectedServiceData.category.child.child?.id,
								subtype: selectedServiceCategoryParameterValue?.categoryParameterValueID
							})
							pushToDataLayer(event)
						}
					}}
				>
					{messages.Continue}
				</Button>
			}
		>
			<SC.Form id={FORM.RESERVATION_BOOKING_MODAL_SERVICE_PARAMETER_SELECTION} onSubmit={handleSubmit(handleSubmitParameterSelectionFrom)}>
				<HookFormField
					control={control}
					name={'serviceCategoryParameterValueID'}
					component={RadioButtonsFormField}
					options={serviceCategoryParameterOptions}
					inputStyle={'bordered'}
					label={serviceCategoryParameterData.name ?? messages['Service parameter']}
					labelScreenReaderOnly
				/>
			</SC.Form>
			{promoBanner.link ? (
				<SC.PromoBannerLink href={promoBanner.link} {...getLinkAttributes(promoBanner.link, productionEnvironment, locale)}>
					{picture}
				</SC.PromoBannerLink>
			) : (
				picture
			)}
		</ReservationBookingModalLayout>
	)
}

export default Step1ServiceParameterSelection
